import React, { useEffect, useState } from 'react';
import Page from 'components/page';

import { productType } from 'interfaces/card-products';
import { CardFlag } from '@orbita-ui/core';
import useGetCardProduct from 'hooks/data/useGetCardProduct';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, DatePicker, Row, Col, Form, Input, Switch, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import useUpdateCardProduct from '../../hooks/update/useUpdateCardProduct';
import useToastContext from '../../hooks/useToastContext';
import useGetCardAffinityGroups from '../../hooks/CardAffinityGroup/useGetCardAffinityGroups';
import { filterOption } from '../../features/card-products/new/first-step';
import InputNumber from 'antd/lib/input-number';

export const NoEditableFieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const WhiteDisabledInput = styled.div`
    input[type="text"][disabled] {
        background-color: white !important;
    }
`;

export const SwitchContainer = styled.div`
    margin-top: 10px;
    p {
        margin-bottom: 5px;
    }
`;

export const SubmitContainer = styled.div `
    width: 100%;
    text-align: right;
    margin: 20px;
`;

type CardProductFormInputs = {
  id: string,
  name: string,
  bin: string,
  range: string,
  design: string,
  startDate: string,
  endDate: Dayjs,
  brand: string,
  maxRiskVisa: number,
  active: boolean,
  productType: string,
  lifeCycle: number,
  recordingCode: number,
  logo: string,
  cardAffinityGroupId: number
}

const CardProduct = () => {
  const toast = useToastContext();
  const { cardProductId } = useParams<{ issuer: string; cardProductId: string }>();
  const { cardProduct } = useGetCardProduct(cardProductId);
  const [active, setActive] = useState(cardProduct?.active);
  const [form] = Form.useForm();
  const { isLoading: loading, isError: error, updateCardProduct } = useUpdateCardProduct(cardProductId);
  const { affinityGroups, loading: loadingAffGroup} = useGetCardAffinityGroups(0, 100, null);

  useEffect(() => {
    form.setFieldValue('id', cardProduct?.id);
    form.setFieldValue('name', cardProduct?.name);
    form.setFieldValue('bin', cardProduct?.bin);
    form.setFieldValue('range', cardProduct?.range);
    form.setFieldValue('design', cardProduct?.design);
    form.setFieldValue('productType', cardProduct?.product_type);
    form.setFieldValue('startDate', cardProduct?.start_date);
    form.setFieldValue('endDate', dayjs(cardProduct?.end_date, 'YYYY-MM-DD'));
    form.setFieldValue('brand', cardProduct?.brand);
    form.setFieldValue('maxRiskVisa', cardProduct?.max_risk_visa);
    form.setFieldValue('lifeCycle', cardProduct?.life_cycle);
    form.setFieldValue('recordingCode', cardProduct?.recording_code);
    form.setFieldValue('active', cardProduct?.active);
    form.setFieldValue('logo', cardProduct?.logo);
    form.setFieldValue('cardAffinityGroupId', cardProduct?.card_affinity_group_id);

  }, [cardProduct]);

  const onSubmit = async (data: CardProductFormInputs) => {
    try {
      const response = await updateCardProduct( {
        name: data?.name,
        range: data?.range,
        design: data?.design,
        active: active,
        logo: data?.logo,
        end_date: dayjs(data?.endDate).format("YYYY-MM-DD"),
        max_risk_visa: data?.maxRiskVisa,
        product_type: data?.productType,
        life_cycle: data?.lifeCycle,
        recording_code: data?.recordingCode,
        card_affinity_group_id: data?.cardAffinityGroupId
      });

      if(response.status === 200 || response.status === 204) {
        toast.success('Exito', 'Producto actualizado correctamente!.');
      } else {
        throw new Error();
      }
    }catch (e: unknown) {
      toast.error('Error', `Ocurrió un error: ${error}, porfavor, intenta nuevamente.`);
    }
  }

  // @ts-ignore
  return (
    <Page title="Card Products">
      {cardProduct?.brand === 'VISA' ? <CardFlag name="Visa" /> : cardProduct?.brand === 'MASTERCARD' ? <CardFlag name="Mastercard" /> : '' }
      <Form<CardProductFormInputs>
        name="basic"
        layout="vertical"
        form={ form }
        onFinish={onSubmit}
        onFinishFailed={console.log}
        style={{ maxWidth: '100%' }}
      >
        <Row gutter={32}>
          <Col span={8}>
            <WhiteDisabledInput>
              <Form.Item<CardProductFormInputs>
                name="id"
                label={'ID'}
              >
                <Input disabled />
              </Form.Item>
            </WhiteDisabledInput>
            <WhiteDisabledInput>
              <Form.Item<CardProductFormInputs>
                name="startDate"
                label={'Fecha desde'}
              >
                <Input disabled />
              </Form.Item>
            </WhiteDisabledInput>
            <Form.Item<CardProductFormInputs>
              name="productType"
              label={'Producto'}
            >
              <Select
                placeholder="Seleccione tipo de producto"
                options={productType}
              />
            </Form.Item>
            <Form.Item<CardProductFormInputs>
              name="recordingCode"
              label={'Codigo de grabación'}
              rules={[
                { required: true, message: 'Por favor ingresar un número.' },
                { type: 'number', min: 0, max: 9999, message: 'Ingrese un número entre 0 y 9999.' },
              ]}
            >
              <InputNumber style={{ minWidth: '100%' }} controls={false}  />
            </Form.Item>
            <Form.Item<CardProductFormInputs>
              name="logo"
              label={'Logo'}
              rules={[{ min:1, max: 4}]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <WhiteDisabledInput>
              <Form.Item<CardProductFormInputs>
                name="brand"
                label={'Marca'}
              >
                <Input disabled />
              </Form.Item>
            </WhiteDisabledInput>
            <Form.Item<CardProductFormInputs>
              name="endDate"
              label={'Fecha hasta'}
            >
              <DatePicker style={{minWidth: '100%', maxWidth: '100%'}} />
            </Form.Item>
            <Form.Item<CardProductFormInputs>
              name="design"
              label={'Diseño'}
            >
              <Input  />
            </Form.Item>
            <Form.Item<CardProductFormInputs>
              name="range"
              label={'Rango'}
            >
              <Input  />
            </Form.Item>
            <Form.Item<CardProductFormInputs>
              name="active"
              valuePropName={cardProduct?.active}
            >
              <SwitchContainer>
                <p>Activo</p>
                <Switch defaultChecked onChange={() => setActive(!active) } />
              </SwitchContainer>
            </Form.Item>
          </Col>
          <Col span={8}>
            <WhiteDisabledInput>
              <Form.Item<CardProductFormInputs>
                name="bin"
                label={'Bin'}
              >
                <Input disabled />
              </Form.Item>
            </WhiteDisabledInput>
            <Form.Item<CardProductFormInputs>
              name="name"
              label={'Nombre'}
            >
              <Input />
            </Form.Item>
            {
              cardProduct?.brand === 'VISA' ?
                <Form.Item<CardProductFormInputs>
                  name="maxRiskVisa"
                  label={'Maximo de riesgo'}
                >
                  <Input />
                </Form.Item>
                :''
            }
            <Form.Item<CardProductFormInputs>
              name="lifeCycle"
              label={'Ciclo de vida (años)'}
            >
              <Input />
            </Form.Item>
            <Form.Item<CardProductFormInputs>
              label="Grupo de afinidad"
              name="cardAffinityGroupId"
            >
              <Select
                value={ cardProduct && cardProduct?.card_affinity_group_id }
                placeholder="Seleccionar"
                showSearch
                filterOption={filterOption}
                loading={loadingAffGroup}
                options={affinityGroups?.map((affg) => ({ label: affg.name, value: affg.id }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <NoEditableFieldsContainer>
          <SubmitContainer>
            <Button type="primary" htmlType="submit" loading={loading}>
              Guardar
            </Button>
          </SubmitContainer>
        </NoEditableFieldsContainer>
      </Form>
    </Page>
  );
};

export default CardProduct;
